import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Plane } from 'lucide-react';
import { getApiBase } from '../../utils/api';
import { noAutofillProps } from '../../utils/noAutofill';
import { usePortalContainer } from './PortalContainerContext';

const AirportAutocomplete = ({
  placeholder = "Search airports...",
  value = "",
  onAirportSelect,
  onValueChange,
  className = "",
  disabled = false
}) => {
  const [searchQuery, setSearchQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [airports, setAirports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [useAbsolutePositioning, setUseAbsolutePositioning] = useState(false);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);
  // Refs to prevent race conditions during selection
  const selectingRef = useRef(false);
  const justSelectedRef = useRef(false);
  // Get portal container from context (inside Dialog) or fall back to document.body
  const portalContainer = usePortalContainer();

  // Update dropdown position when open changes or airports load
  // When inside a Radix Dialog (which uses CSS transform), we need to calculate
  // position relative to the portal container, not the viewport
  useEffect(() => {
    if (isOpen && inputRef.current && portalContainer) {
      const inputRect = inputRef.current.getBoundingClientRect();

      // If portal container is document.body, use viewport-relative fixed positioning
      // Otherwise, calculate position relative to the portal container (absolute positioning)
      if (portalContainer === document.body) {
        setUseAbsolutePositioning(false);
        setDropdownPosition({
          top: inputRect.bottom,
          left: inputRect.left,
          width: inputRect.width
        });
      } else {
        // We're inside a custom container (likely inside a Dialog with CSS transform)
        // Calculate position relative to the container and use absolute positioning
        setUseAbsolutePositioning(true);
        const containerRect = portalContainer.getBoundingClientRect();
        setDropdownPosition({
          top: inputRect.bottom - containerRect.top,
          left: inputRect.left - containerRect.left,
          width: inputRect.width
        });
      }
    }
  }, [isOpen, airports, portalContainer]);

  // Update search query when value prop changes
  // Skip syncing if we just made a selection (to preserve the display format)
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setSearchQuery(value);
  }, [value]);

  const searchAirports = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setAirports([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const apiBase = getApiBase();

      const url = `${apiBase}/airports/search?query=${encodeURIComponent(query)}&private_only=true&limit=15`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAirports(data || []);
      } else {
        setAirports([]);
      }
    } catch (error) {
      console.error('Error searching airports:', error);
      setAirports([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setSelectedIndex(-1);
    
    // Notify parent of value change
    if (onValueChange) {
      onValueChange(newValue);
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce search
    timeoutRef.current = setTimeout(() => {
      searchAirports(newValue);
    }, 300);
  }, [searchAirports, onValueChange]);

  const handleAirportSelect = useCallback((airport) => {
    if (selectingRef.current) return; // guard against duplicate firing
    selectingRef.current = true;

    // Format display as "Airport Name (ICAO)"
    const displayValue = `${airport.name} (${airport.icao_code || airport.iata_code || 'N/A'})`;
    setSearchQuery(displayValue);
    setIsOpen(false);
    setAirports([]); // clear results to avoid auto-reopen
    setSelectedIndex(-1);
    justSelectedRef.current = true;

    // Notify parent with complete airport data
    if (onAirportSelect) {
      onAirportSelect({
        ...airport,
        displayName: displayValue
      });
    }

    if (onValueChange) {
      onValueChange(displayValue);
    }

    // Release the guard after the macrotask so blur/click won't re-fire
    setTimeout(() => { selectingRef.current = false; }, 0);
  }, [onAirportSelect, onValueChange]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen || airports.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < airports.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : airports.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < airports.length) {
          handleAirportSelect(airports[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  }, [isOpen, airports, selectedIndex, handleAirportSelect]);

  const handleFocus = useCallback(() => {
    // Don't reopen dropdown immediately after a selection
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setIsOpen(true);
    if (searchQuery && searchQuery.length >= 2) {
      searchAirports(searchQuery);
    }
  }, [searchQuery, searchAirports]);

  const handleBlur = useCallback((e) => {
    // Delay hiding dropdown to allow clicks on dropdown items (including portal)
    setTimeout(() => {
      const activeEl = document.activeElement;
      const inDropdown = dropdownRef.current?.contains(activeEl);
      const inInput = inputRef.current?.contains(activeEl);
      if (!inDropdown && !inInput) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    }, 200);
  }, []);

  // Show dropdown only when the input is focused (avoid auto-opening after selection)
  useEffect(() => {
    // Don't auto-open right after a selection was made
    if (justSelectedRef.current) {
      return;
    }
    const inputFocused = document.activeElement === inputRef.current;
    if ((airports.length > 0 || isLoading) && inputFocused) {
      setIsOpen(true);
    } else if (!inputFocused) {
      setIsOpen(false);
    }
  }, [airports, isLoading]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          {...noAutofillProps}
          name="airport-search"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full pl-10 pr-4 py-3 border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-violet-solid focus:border-transparent text-ui-ink placeholder-ui-faint ${className}`}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Plane className="h-5 w-5 text-ui-muted" />
        </div>
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-violet-solid"></div>
          </div>
        )}
      </div>

      {/* Dropdown - rendered in portal for proper z-index in dialogs */}
      {isOpen && portalContainer && createPortal(
        <div
          ref={dropdownRef}
          data-autocomplete-dropdown="true"
          className={`${useAbsolutePositioning ? 'absolute' : 'fixed'} z-[9999] bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-64 overflow-y-auto`}
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
          onMouseDown={(e) => {
            // Prevent blur when interacting with dropdown (scrolling, clicking)
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {isLoading && (
            <div className="px-4 py-3 text-ui-muted text-center">
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ui-violet-solid"></div>
                Searching airports...
              </div>
            </div>
          )}

          {!isLoading && airports.length === 0 && searchQuery.length >= 2 && (
            <div className="px-4 py-3 text-ui-muted text-center">
              No airports found for "{searchQuery}"
            </div>
          )}

          {!isLoading && airports.length > 0 && (
            <>
              <div className="px-3 py-2 bg-ui-sunk border-b border-ui-line">
                <div className="text-xs font-medium text-ui-ink-3">
                  Found {airports.length} airport{airports.length !== 1 ? 's' : ''}
                </div>
              </div>
              {airports.map((airport, index) => (
                <div
                  key={airport.id || `${airport.icao_code}-${airport.iata_code}`}
                  onMouseDown={(e) => {
                    // Handle selection on mousedown to avoid Radix Dialog focus trap issues
                    e.preventDefault();
                    e.stopPropagation();
                    handleAirportSelect(airport);
                  }}
                  className={`px-4 py-3 cursor-pointer border-b border-ui-divider last:border-b-0 hover:bg-ui-violet-bg ${
                    index === selectedIndex ? 'bg-ui-violet-bg' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-8 h-8 bg-gradient-to-br from-ui-violet-strong to-ui-indigo-strong rounded-full flex items-center justify-center">
                        <MapPin className="w-4 h-4 text-ui-on-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-medium text-ui-ink truncate">
                          {airport.name}
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                          {airport.icao_code && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-blue-bg text-ui-blue-fg">
                              {airport.icao_code}
                            </span>
                          )}
                          {airport.iata_code && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-ui-green-bg text-ui-green-fg">
                              {airport.iata_code}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-ui-ink-3">
                        {airport.municipality && airport.country_name ? (
                          `${airport.municipality}, ${airport.country_name}`
                        ) : (
                          airport.country_name || 'Unknown location'
                        )}
                      </div>
                      {airport.airport_type && (
                        <div className="text-xs text-ui-muted mt-1">
                          {airport.airport_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          {airport.private_aircraft_access && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-ui-green-bg text-ui-green-fg">
                              Private OK
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {searchQuery.length < 2 && (
            <div className="px-4 py-3 text-ui-muted text-center">
              Type at least 2 characters to search airports
            </div>
          )}
        </div>,
        portalContainer
      )}
    </div>
  );
};

export default AirportAutocomplete;